import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Advertisers.css';

interface Advertiser {
  id: number;
  name: string;
  contact_email: string;
  status: 'active' | 'paused' | 'blocked';
  monthly_budget?: number;
  current_month_spend?: number;
  budget_status?: 'active' | 'warning' | 'exceeded';
  budget_alert_threshold?: number;
  created_at: string;
}

const Advertisers: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    fetchAdvertisers();
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchAdvertisers = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdvertisers({
        limit: itemsPerPage,
        page: currentPage
      });
      const advertisersWithBudgetStatus = response.advertisers.map((advertiser: any) => {
        const budgetPercent = advertiser.monthly_budget ?
          (parseFloat(advertiser.current_month_spend) / parseFloat(advertiser.monthly_budget)) * 100 : 0;

        let budget_status: 'active' | 'warning' | 'exceeded' = 'active';
        if (budgetPercent >= 100) budget_status = 'exceeded';
        else if (budgetPercent >= (advertiser.budget_alert_threshold || 80)) budget_status = 'warning';

        return {
          ...advertiser,
          budget_status
        };
      });
      setAdvertisers(advertisersWithBudgetStatus);
      if (response.pagination) {
        setTotalPages(response.pagination.totalPages);
        setTotalCount(response.pagination.total);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch advertisers');
    } finally {
      setLoading(false);
    }
  };

  const filteredAdvertisers = advertisers.filter(advertiser =>
    advertiser.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    advertiser.contact_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleStatus = async (id: number) => {
    const advertiser = advertisers.find(a => a.id === id);
    if (!advertiser) return;

    let newStatus: 'active' | 'paused' | 'blocked';
    switch (advertiser.status) {
      case 'active':
        newStatus = 'paused';
        break;
      case 'paused':
        newStatus = 'blocked';
        break;
      case 'blocked':
        newStatus = 'active';
        break;
      default:
        newStatus = 'active';
    }

    try {
      await apiService.updateAdvertiser(id, { status: newStatus });
      setAdvertisers(advertisers.map(adv =>
        adv.id === id ? { ...adv, status: newStatus } : adv
      ));
    } catch (err: any) {
      alert(`Failed to update advertiser status: ${err.message}`);
    }
  };

  const handleView = (id: number) => {
    navigate(`/advertisers/view/${id}`);
  };

  const handleEdit = (id: number) => {
    navigate(`/advertisers/edit/${id}`);
  };

  const handleAddNew = () => {
    navigate('/advertisers/add');
  };

  return (
    <div className="advertisers-page">
      <div className="page-header">
        <h1>All Advertisers</h1>
        <button className="btn btn-primary" onClick={handleAddNew}>
          <span className="btn-icon">➕</span>
          Add Advertiser
        </button>
      </div>

      <div className="advertisers-section">
        <div className="section-header">
          <div className="export-buttons">
            <button className="btn btn-success">Copy</button>
            <button className="btn btn-info">CSV</button>
            <button className="btn btn-warning">Excel</button>
            <button className="btn btn-danger">PDF</button>
            <button className="btn btn-secondary">Print</button>
          </div>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          <table className="advertisers-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Budget Status</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                    Loading advertisers...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
                    Error: {error}
                  </td>
                </tr>
              ) : filteredAdvertisers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '20px' }}>
                    No advertisers found
                  </td>
                </tr>
              ) : (
                filteredAdvertisers.map((advertiser) => (
                  <tr key={advertiser.id}>
                    <td>{advertiser.id}</td>
                    <td>{advertiser.name}</td>
                    <td>{advertiser.contact_email}</td>
                    <td>
                      {advertiser.monthly_budget ? (
                        <div style={{ fontSize: '12px' }}>
                          <div style={{
                            color: advertiser.budget_status === 'exceeded' ? '#e74c3c' :
                                   advertiser.budget_status === 'warning' ? '#f39c12' : '#27ae60'
                          }}>
                            ${parseFloat(String(advertiser.current_month_spend || '0')).toLocaleString()} / ${parseFloat(String(advertiser.monthly_budget)).toLocaleString()}
                          </div>
                          <span className={`status-badge ${
                            advertiser.budget_status === 'exceeded' ? 'danger' :
                            advertiser.budget_status === 'warning' ? 'warning' : 'success'
                          }`} style={{ fontSize: '10px', padding: '1px 4px' }}>
                            {Math.round(((parseFloat(String(advertiser.current_month_spend || '0'))) / parseFloat(String(advertiser.monthly_budget))) * 100)}%
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#a0aec0', fontSize: '12px' }}>No budget set</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`status-badge ${advertiser.status}`}
                        onClick={() => toggleStatus(advertiser.id)}
                      >
                        {advertiser.status.charAt(0).toUpperCase() + advertiser.status.slice(1)}
                        <span className="status-toggle">Change Status</span>
                      </button>
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {new Date(advertiser.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="action-links">
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => handleEdit(advertiser.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="action-link"
                          onClick={() => handleView(advertiser.id)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="table-footer">
          <div className="table-info">
            Showing {filteredAdvertisers.length > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries
          </div>
          <div className="pagination">
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="page-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Advertisers;
